import { PImage } from "../../../../../shared/PImage/PImage";
import { useCategoryItemContext } from "../../../../../../services/context/CategoryItemContext";
import { useCustomerAnalytics } from "../../../../../../services/hooks/useCustomerAnalytics";
import { useModalHook } from "../../../../../../services/hooks/useModal";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../../../services/hooks/useTranslationI18";
import {
  setBottomSheetSource,
  setBottomSheetState,
} from "../../../../../../services/redux/slices/globalComponentsToggleSlice";
import { setSelectedItemTimeBoundData } from "../../../../../../services/redux/slices/itemStatusSlice";
import { TransformOfferType } from "../../../../../../services/redux/types/TypeHelper";
import CartItem from "../../../../../../utils/CartItem";
import { cn } from "../../../../../../utils/cn";
import {
  CUSTOMER_ANALYTICS_EVENT,
  ChangeCounterFrom,
} from "../../../../../../utils/enums";
import { isValidOffer } from "../../../../../../utils/help/offers/offers";
import { ItemOfferV1Type, OFFER_TYPE } from "../../../../../../utils/types";
import { CounterV2 } from "../../../../../global/Counter/CounterV2";
import { DiscountedPriceV2 } from "../../../../../global/DiscountedPrice/DiscountedPriceV2";
import { ItemName } from "../../../../../shared/ItemName/ItemName";
import { ItemTagChip } from "../../../../../shared/ItemTagChip/ItemTagChip";
import { CategoryItemText } from "../CategoryItemDetails/CategoryItemDetails";
import { endsWithGif } from "../CategoryItemDetails/CategoryItemDetailsHelper";
import { LabelV2 } from "../Label/LabelV2";
import { useCategoryItemCard } from "../useCategoryItemCard";
import styles from "./CategoryItemCardV2.module.scss";

export const CategoryItemCardV2Grid = () => {
  const { item, categoryId, index, addItem } = useCategoryItemContext();
  const dispatch = useAppDispatch();
  const addCustomerAnalytics = useCustomerAnalytics();
  const { renderWebViewItem } = useModalHook();
  const { t } = useTranslationI18();

  const {
    itemDetails,
    isOutOfStock,
    cartCountExcludingOffers,
    cartItemIndexExcludingOffers,
    cartItemExcludingOffers,
    shouldOpenBottomSheet,
    canAddToCart,
    hidePrice,
    isMobile,
    isEcommerce,
  } = useCategoryItemCard();

  const offerGet = t("common:offer-get");
  const offerOff = t("common:offer-off");

  const handleCardClick = () => {
    if (shouldOpenBottomSheet) {
      dispatch(setBottomSheetSource("item_selection"));
      dispatch(setBottomSheetState(true));

      return;
    }

    dispatch(setSelectedItemTimeBoundData(item.timeBoundAvailabilityData));
    !isMobile && renderWebViewItem(itemDetails.id);
  };

  const handleAddClick = (e?: React.MouseEvent) => {
    if (isMobile) {
      e?.preventDefault();
    } else {
      e?.stopPropagation();
    }

    if (!canAddToCart) {
      return;
    }

    if (shouldOpenBottomSheet) {
      addCustomerAnalytics(
        CUSTOMER_ANALYTICS_EVENT.ADD_ITEM_ATTEMPT_BEFORE_BRANCH_AND_ORDER_TYPE_SELECTION
      );
      dispatch(setBottomSheetSource("item_selection"));
      dispatch(setBottomSheetState(true));

      return;
    }

    if (cartCountExcludingOffers > 0) {
      return;
    }

    dispatch(setSelectedItemTimeBoundData(item.timeBoundAvailabilityData));
    addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.ADD_ITEM_FROM_MENU);
    addItem(e, index);
  };

  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const isMainDark = restaurantInfo?.colors?.theme === "main_dark";

  return (
    <div
      onClick={handleCardClick}
      className={cn(styles.grid_item, isMainDark && "invert")}
      id={categoryId.toString()}
      key={itemDetails.img_url + index}
    >
      <div className={styles.img_container}>
        <PImage
          id={`next-img-${itemDetails.id}`}
          src={itemDetails.img_url}
          alt={itemDetails.item_name}
          fill
          style={{ objectFit: "cover", objectPosition: "center" }}
          unoptimized={endsWithGif(itemDetails.img_url)}
          quality="100"
          className={styles.img}
          sizes="(max-width: 900px) 33vw, (max-width: 1200px) 20vw"
        />
        <div className={styles.chips_row}>
          <LabelV2 />
          <ItemTagChip item={itemDetails} isOutOfStock={isOutOfStock} />
        </div>
        <div
          className={styles.counter_overlay}
          onClick={(e) => handleAddClick(e)}
        >
          <CounterV2
            count={cartCountExcludingOffers}
            itemIndex={cartItemIndexExcludingOffers}
            data={cartItemExcludingOffers as TransformOfferType<CartItem>}
            changeCounterView={ChangeCounterFrom.CartView}
            isDisabled={!canAddToCart}
            size="lg"
            layout="horizontal"
            hasShadow
            includeBackground
          />
        </div>
      </div>

      <div className={styles.grid_item__content}>
        <div className={styles.grid_item__text_content}>
          <span
            className={styles.list_item__name}
            data-testid={`menu-item-${itemDetails.item_name}`}
          >
            <ItemName item={item} />
          </span>
          {isValidOffer(itemDetails?.offer as ItemOfferV1Type) && (
            <p className={styles.list_item__offer_text}>
              {offerGet} {itemDetails.offer?.offerValue}
              {itemDetails.offer?.offerType === OFFER_TYPE.PERCENTAGE
                ? "%"
                : ""}{" "}
              {offerOff}
            </p>
          )}
        </div>
        {!hidePrice && (
          <div className={styles.list_item__price}>
            <DiscountedPriceV2
              item={itemDetails}
              isValidOffer={isValidOffer(itemDetails.offer as ItemOfferV1Type)}
            />
          </div>
        )}
      </div>
      {isEcommerce && <CategoryItemText />}
    </div>
  );
};
